import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import type { EmailVerification } from '@feathr/blackbox';
import { Button, DebouncedInput, Input, TanStackTable } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import type { TCollectionTableSort } from '@feathr/extender/hooks';
import { useCollectionTable } from '@feathr/extender/hooks';
import { useLocalUrl, useStore } from '@feathr/extender/state';
import { getIconForAction } from '@feathr/hooks';

import emailsColumns from './emailsColumns';
import { getSearchFilters } from './EmailsPage.utils';

import * as styles from './EmailsPage.css';

const PAGE_SIZE = 10;
const INITIAL_SORT: TCollectionTableSort = [{ desc: false, id: 'email' }];

function EmailsPage(): JSX.Element {
  const { EmailVerifications } = useStore();
  const history = useHistory();
  const localUrl = useLocalUrl();
  const [search, setSearch] = useState<string | undefined>();
  const { t } = useTranslation();

  function handleAddEmail(): void {
    const newEmail = EmailVerifications.create();
    history.push(localUrl(newEmail.getItemUrl('edit')));
  }

  function handleDebouncedSearchChange(newValue?: string): void {
    setSearch(newValue);
  }

  const tableProps = useCollectionTable<EmailVerification>(EmailVerifications, {
    defaultParams: { filters: getSearchFilters(search) },
    initialPageSize: PAGE_SIZE,
    initialSort: INITIAL_SORT,
  });

  const columns = useMemo(() => emailsColumns(t), [t]);

  const filterElements = [
    null,
    <DebouncedInput<string>
      defaultValue={search}
      key={'search'}
      onChange={handleDebouncedSearchChange}
    >
      {(liveValue, onChangeLiveValue): JSX.Element => (
        <Input
          className={styles.filter}
          isClearable={true}
          onChange={onChangeLiveValue}
          placeholder={t('Search by name or email...')}
          prefix={getIconForAction('search')}
          type={'text'}
          value={liveValue}
        />
      )}
    </DebouncedInput>,
  ];

  return (
    <Page
      actions={[
        <Button
          key={'add'}
          onClick={handleAddEmail}
          prefix={getIconForAction('add')}
          type={'primary'}
        >
          {t('Add email address')}
        </Button>,
      ]}
      collapseHeader={false}
      collapseHeaderOnScroll={true}
      description={t(
        'Setup and manage emails that you can use as the from address on email campaigns.',
      )}
      title={t('Email Addresses')}
    >
      <TanStackTable<EmailVerification>
        {...tableProps}
        columns={columns}
        filterElements={filterElements}
        noDataText={t('No email addresses have been verified.')}
      />
    </Page>
  );
}

export default observer(EmailsPage);
